import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import ProductCard from "../components/ProductCard";
import CategoryTechExplainer from "../components/CategoryTechExplainer";
import { CATEGORY_TECH } from "../config/categoryTech";
import { fetchProducts, fetchCategories } from "../lib/api";
import { useLang } from "../context/I18nContext";

// Display order for the "All Products" view — most important on top
const CATEGORY_ORDER = [
  "vollsystem-wp",
  "warmwasser-wp",
  "warmwasser-speicher",
  "heizung-gas",
  "ems-pv",
  "automation",
  "licht",
  "sensorik",
  "zutritt",
  "beschattung",
];

export default function Shop() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get("cat") || "all";
    setActive(cat);
  }, [location.search]);

  useEffect(() => {
    Promise.all([fetchProducts(), fetchCategories()]).then(([p, c]) => {
      setProducts(p); setCats(c); setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    let list = products;
    if (active !== "all") list = list.filter((p) => p.category === active);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || p.short_description.toLowerCase().includes(q));
    }
    return list;
  }, [products, active, search]);

  // For "All Products" view: group products by category following CATEGORY_ORDER
  const grouped = useMemo(() => {
    if (active !== "all") return null;
    const byCat = {};
    filtered.forEach((p) => {
      if (!byCat[p.category]) byCat[p.category] = [];
      byCat[p.category].push(p);
    });
    // Return in fixed order, only categories with at least 1 product
    return CATEGORY_ORDER
      .filter((c) => byCat[c] && byCat[c].length > 0)
      .map((c) => ({ key: c, items: byCat[c] }));
  }, [filtered, active]);

  const setCat = (key) => {
    if (key === "all") navigate("/shop");
    else navigate(`/shop?cat=${key}`);
  };

  return (
    <div className="page-shell" data-testid="shop-page">
      <section className="site-container page-hero">
        <div className="eyebrow mb-6 text-[#5FA637]">{t("shop.eyebrowProducts")} · {products.length}</div>
        <h1 className="page-title">
          {t("shop.headline1")} <span className="italic-accent">{t("shop.headline2")}</span>
        </h1>
        <p className="copy-lead mt-8 text-base md:text-lg">
          {t("shop.subtitle")}
        </p>
        <div className="mt-6 inline-flex items-center gap-3 border border-white/15 px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase" data-testid="netto-disclaimer">
          <span className="w-2 h-2 bg-[#5FA637] rounded-full animate-pulse"></span>
          <span className="text-white/85">{t("shop.nettoDisclaimer")}</span>
        </div>
      </section>

      <section className="site-container py-8">
        <div className="flex flex-col md:flex-row md:items-center gap-5 mb-8">
          <div className="eyebrow shrink-0">{t("shop.categoryLabel")}</div>
          <div className="flex-1 flex flex-wrap gap-2">
            <button onClick={() => setCat("all")} className={`btn-pill ${active === "all" ? "active" : ""}`} data-testid="filter-all">{t("shop.allCategories")}</button>
            {cats.map((c) => (
              <button
                key={c.key}
                onClick={() => setCat(c.key)}
                data-testid={`filter-${c.key}`}
                className={`btn-pill ${active === c.key ? "active" : ""}`}
              >
                {t(`cat.${c.key}`, c.name)}
              </button>
            ))}
          </div>
          <div className="relative shrink-0">
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={t("shop.searchPlaceholder")}
              className="bg-transparent border border-white/15 hover:border-white/30 focus:border-white text-sm px-4 py-3 w-full md:w-64 outline-none transition-colors"
              data-testid="shop-search"
            />
            {search && (
              <button onClick={()=>setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white" data-testid="shop-search-clear" aria-label="Suche löschen">
                <X size={14}/>
              </button>
            )}
          </div>
        </div>
      </section>

      {active !== "all" && CATEGORY_TECH[active] && (
        <CategoryTechExplainer config={CATEGORY_TECH[active]} categoryKey={active} />
      )}

      <section className="site-container">
        {loading ? (
          <div className="py-24 text-center text-white/50 text-sm tracking-[0.15em] uppercase" data-testid="shop-loading">{t("shop.loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="py-24 text-center text-white/50" data-testid="shop-empty">{t("shop.empty")}</div>
        ) : grouped ? (
          <div data-testid="shop-grouped">
            {grouped.map((group) => (
              <div key={group.key} className="mb-14 reveal" data-testid={`group-${group.key}`}>
                <div className="flex items-end justify-between mb-6 pb-4 border-b border-white/10">
                  <div>
                    <div className="eyebrow mb-2">{t(`cat.${group.key}`, group.key)}</div>
                    <h2 className="font-display text-2xl md:text-3xl tracking-tight">
                      {group.items.length} {group.items.length === 1 ? "Produkt" : "Produkte"}
                    </h2>
                  </div>
                  <button
                    onClick={() => setCat(group.key)}
                    className="text-[11px] tracking-[0.15em] uppercase border-b border-[#5FA637]/60 pb-1 text-[#5FA637] hover:border-[#5FA637]"
                    data-testid={`group-jump-${group.key}`}
                  >
                    System planen →
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-white/10 border border-white/10 reveal-stagger">
                  {group.items.map((p) => <ProductCard key={p.id} product={p}/>)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {filtered.map((p) => <ProductCard key={p.id} product={p}/>)}
          </div>
        )}
      </section>
    </div>
  );
}
